/**
 * Gamification Types
 *
 * Types for the DoCurious XP, level, badge, streak, and leaderboard systems.
 * Based on Doc 8: Gamification System -- 15 levels, 7 badge categories,
 * weekly streaks, and Dealer's Choice multipliers.
 *
 * @packageDocumentation
 */

import type { BaseEntity, UUID } from './common.types'

/**
 * Badge classification (7 main categories + 2 special per Doc 8).
 *
 * @remarks
 * Each badge belongs to exactly one category. Categories drive how badges
 * are grouped in the user's badge showcase and profile.
 *
 * - `milestone` -- Earned by reaching completion milestones (e.g., 10 challenges)
 * - `category_mastery` -- Earned by completing N challenges in a single category
 * - `breadth` -- Earned by completing challenges across N different categories
 * - `streak` -- Earned by maintaining weekly activity streaks
 * - `dealer_choice` -- Earned through Dealer's Choice random challenges
 * - `path` -- Earned by completing Learning Path milestones
 * - `cooperation` -- Earned by helping others (verification, community contributions)
 * - `special` -- Limited-time or special event badges
 * - `challenge_specific` -- Earned by completing a particular challenge
 */
export type BadgeCategory =
  | 'milestone'
  | 'category_mastery'
  | 'breadth'
  | 'streak'
  | 'dealer_choice'
  | 'path'
  | 'cooperation'
  | 'special'
  | 'challenge_specific'

/**
 * Badge rarity tier, which also determines the XP bonus awarded when earned.
 *
 * @remarks
 * XP bonuses by rarity: common=10, uncommon=25, rare=50, epic=100, legendary=250.
 * See {@link XP_VALUES} for the full mapping.
 */
export type BadgeRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'

/**
 * A badge definition that specifies earning criteria, rarity, and display properties.
 *
 * @remarks
 * Badges are admin-managed templates. When a user meets the `criteria`, a
 * {@link UserBadge} record is created and XP is awarded based on rarity.
 *
 * @example
 * ```ts
 * const badge: Badge = {
 *   id: 'bdg-005',
 *   createdAt: '2025-01-01T00:00:00Z',
 *   updatedAt: '2025-01-01T00:00:00Z',
 *   name: 'Trailblazer',
 *   description: 'Complete challenges in 5 different categories.',
 *   category: 'breadth',
 *   rarity: 'rare',
 *   criteria: { type: 'category_count', value: 5 },
 *   xpBonus: 50,
 * }
 * ```
 */
export interface Badge extends BaseEntity {
  name: string
  description: string
  iconUrl?: string
  category: BadgeCategory
  rarity: BadgeRarity

  /** Rule that determines when the badge is awarded */
  criteria: {
    /** The metric type to evaluate */
    type: 'challenge' | 'challenge_count' | 'category_count' | 'streak' | 'verification_count' | 'special'
    /** Specific challenge ID (for `challenge_specific` badges) */
    challengeId?: string
    /** Specific category ID (for `category_mastery` badges) */
    categoryId?: string
    /** Threshold value that must be reached to earn the badge */
    value: number
  }

  /** XP bonus awarded when earned (overrides the default rarity-based bonus if set) */
  xpBonus?: number

  displayOrder?: number

  /** If true, badge is hidden in the showcase until the user earns it */
  isHidden?: boolean

  /** If false, this badge is retired and can no longer be earned */
  isActive?: boolean
}

/**
 * Progress toward earning a specific badge (for unearned badges).
 */
export interface BadgeProgress {
  badgeId: string
  badge: Badge
  /** Current value toward the criteria threshold */
  current: number
  /** Target value required to earn the badge */
  target: number
  /** Progress percentage (0-100) */
  percentage: number
}

/**
 * A badge earned by a specific user, with the timestamp and context of how it was earned.
 */
export interface UserBadge extends BaseEntity {
  userId: string
  badgeId: string
  /** Populated badge definition (loaded for display) */
  badge?: Badge

  /** ISO datetime when the badge was awarded */
  earnedAt: string

  /** Additional context (e.g., which challenge or streak milestone triggered the award) */
  metadata?: Record<string, unknown>
}

/**
 * Discriminator for the type of activity that generated an XP event.
 *
 * @remarks
 * Each type maps to a base XP value in {@link XP_VALUES}. Multipliers
 * (streak, Dealer's Choice, first-of-day) are applied on top.
 */
export type XPEventType =
  | 'challenge_completed'        // Base XP for completing challenge
  | 'milestone_completed'        // Partial XP for milestone
  | 'track_entry_added'          // XP for adding a track record entry
  | 'badge_earned'               // XP from badge
  | 'streak_bonus'               // Streak multiplier
  | 'first_of_day'               // Daily first activity bonus
  | 'community_post'             // Community post XP
  | 'verification_given'         // Verifying someone else's work
  | 'gift_sent'                  // Sending a gift
  | 'referral'                   // Referral bonus

/**
 * A single XP award event, used for activity feeds and recent XP displays.
 */
export interface XPEvent extends BaseEntity {
  userId: string
  eventType: XPEventType
  /** XP amount awarded (always positive) */
  amount: number

  /** Additional context (e.g., `{ challengeId: 'ch-042' }`) */
  metadata?: Record<string, unknown>
}

/**
 * Ledger-style XP transaction with running balance, used for audit and history views.
 *
 * @remarks
 * Unlike {@link XPEvent} (which is display-oriented), XPTransaction tracks
 * the running XP balance after each change and supports negative amounts
 * (e.g., Dealer's Choice abandon penalty).
 */
export interface XPTransaction extends BaseEntity {
  userId: UUID
  eventType: XPEventType
  /** XP change amount -- positive for gains, negative for penalties */
  amount: number

  challengeId?: UUID
  badgeId?: UUID
  /** Human-readable description of what triggered this transaction */
  description: string

  /** Cumulative XP total after this transaction was applied */
  balanceAfter: number
}

/**
 * A single level in the 15-level progression system, with XP thresholds and theme color.
 *
 * @remarks
 * Level names are thematically tied to exploration (Newcomer -> Legend).
 * The `color` is used for level badges, progress bars, and profile accents.
 * See {@link LEVEL_DEFINITIONS} for the complete progression table.
 */
export interface LevelDefinition {
  /** Level number (1-15) */
  level: number
  /** Display name (e.g., "Explorer", "Champion", "Legend") */
  name: string
  /** Minimum cumulative XP to reach this level */
  minXp: number
  /** XP threshold for the next level (Infinity for max level) */
  maxXp: number
  /** Theme color token used for level badges and progress bars */
  color: string
  /** Platform features unlocked at this level */
  featureUnlocks?: string[]
}

/**
 * Summary gamification stats for dashboard widgets and profile cards.
 */
export interface GamificationStats {
  totalXp: number
  /** Current weekly activity streak length */
  currentStreak: number
  /** All-time longest streak achieved */
  longestStreak: number
  badgesEarned: number
  challengesCompleted: number
  /** Most recent XP events for the activity feed */
  recentXpEvents: XPEvent[]
}

/**
 * Comprehensive gamification statistics for a user, including level progress and category breakdown.
 *
 * @remarks
 * This is the full stat payload used by the profile page and detailed stats views.
 * For the lighter dashboard summary, see {@link GamificationStats}.
 */
export interface UserGamificationStats {
  userId: UUID
  totalXp: number
  /** Current level number (1-15) */
  currentLevel: number
  /** XP remaining to reach the next level */
  xpToNextLevel: number
  /** Progress percentage within the current level (0-100) */
  xpProgress: number

  totalChallengesCompleted: number
  /** Distinct challenges completed (excludes repeat completions) */
  uniqueChallengesCompleted: number
  totalBadgesEarned: number

  /** Current weekly activity streak length */
  currentStreak: number
  longestStreak: number
  /** ISO date of the user's most recent qualifying activity */
  lastActivityDate?: string

  /** Number of challenges completed per category (keyed by category UUID) */
  categoryCounts: Record<UUID, number>
}

/**
 * A cooperative group challenge where multiple users tackle a challenge together.
 *
 * @remarks
 * Team XP multipliers scale with group size: 1.25 for 2 members, 1.5 for 3+.
 * The creator is always included in `memberUserIds`.
 */
export interface GroupChallenge extends BaseEntity {
  challengeId: UUID
  creatorUserId: UUID
  name: string
  memberUserIds: UUID[]
  status: 'open' | 'in_progress' | 'completed'
  teamXpMultiplier: number // 1.25 for 2 members, 1.5 for 3+
  completedAt?: string
}

/**
 * Leaderboard scope — controls which subset of users are ranked.
 */
export type LeaderboardScope = 'global' | 'monthly' | 'community' | 'school' | 'friends'

/**
 * Leaderboard type — the metric being ranked.
 */
export type LeaderboardType = 'xp' | 'challenges' | 'streak' | 'badges' | 'community'

/**
 * A single row in a leaderboard ranking (community, school, or global).
 */
export interface LeaderboardEntry {
  /** 1-based rank position */
  rank: number
  userId: string
  displayName: string
  avatarUrl?: string
  /** The metric value being ranked (could be XP, completions, or badges depending on board type) */
  value: number
  totalXp: number
  badgeCount: number
}

/**
 * Level definitions (standard progression) with colors
 * Per Doc 8: Gamification System — 15 levels using cumulative XP thresholds
 * Beyond Level 15: continue with "Legend II", "Legend III" at 30,000 XP increments
 */
export const LEVEL_DEFINITIONS: LevelDefinition[] = [
  { level: 1, name: 'Newcomer', minXp: 0, maxXp: 100, color: 'gray', featureUnlocks: ['basic_challenges'] },
  { level: 2, name: 'Curious', minXp: 100, maxXp: 350, color: 'green', featureUnlocks: ['saved_list', 'community_join'] },
  { level: 3, name: 'Explorer', minXp: 350, maxXp: 850, color: 'teal', featureUnlocks: ['dealers_choice', 'community_post'] },
  { level: 4, name: 'Adventurer', minXp: 850, maxXp: 1600, color: 'blue', featureUnlocks: ['create_community', 'partner_finding'] },
  { level: 5, name: 'Pathfinder', minXp: 1600, maxXp: 2600, color: 'cyan', featureUnlocks: ['learning_paths', 'gift_challenges'] },
  { level: 6, name: 'Trailblazer', minXp: 2600, maxXp: 4100, color: 'purple', featureUnlocks: ['portfolio_builder'] },
  { level: 7, name: 'Pioneer', minXp: 4100, maxXp: 6600, color: 'pink', featureUnlocks: ['verification_giving', 'group_challenges'] },
  { level: 8, name: 'Voyager', minXp: 6600, maxXp: 10100, color: 'orange', featureUnlocks: ['leaderboard_monthly'] },
  { level: 9, name: 'Wayfinder', minXp: 10100, maxXp: 15100, color: 'red', featureUnlocks: ['portfolio_share_link'] },
  { level: 10, name: 'Discoverer', minXp: 15100, maxXp: 22100, color: 'yellow', featureUnlocks: ['custom_badges'] },
  { level: 11, name: 'Seeker', minXp: 22100, maxXp: 32100, color: 'indigo' },
  { level: 12, name: 'Ranger', minXp: 32100, maxXp: 47100, color: 'violet' },
  { level: 13, name: 'Champion', minXp: 47100, maxXp: 67100, color: 'fuchsia' },
  { level: 14, name: 'Master', minXp: 67100, maxXp: 97100, color: 'rose' },
  { level: 15, name: 'Legend', minXp: 97100, maxXp: Infinity, color: 'gold' },
]

/**
 * The highest achievable level in the standard progression (Level 15: "Legend").
 * Beyond this, prestige levels ("Legend II", "Legend III") continue at 30,000 XP increments.
 */
export const MAX_LEVEL = 15

/**
 * Canonical XP award values and multipliers for all gamification events (per Doc 8).
 *
 * @remarks
 * Base challenge XP is determined by difficulty. Dealer's Choice multipliers
 * scale based on how far outside the user's comfort zone the challenge is.
 * Under-13 users use the reduced `dc_under13_practice` multiplier.
 */
export const XP_VALUES = {
  // Base XP by difficulty (per Doc 8: Gamification System)
  challenge_beginner: 50,
  challenge_intermediate: 100,
  challenge_advanced: 200,

  // Activity XP
  track_entry: 5,
  milestone: 10,
  community_post: 3,
  verification_given: 5,

  // Bonuses
  first_in_category: 15,
  first_of_day: 5,
  repeat_multiplier: 0.5,

  // Dealer's Choice multipliers
  dc_comfort_zone: 1.5,
  dc_rarely_tried: 1.75,
  dc_uncharted: 2.0,

  // Penalties
  dc_abandon_multiplier: -1,

  // Under-13 Dealer's Choice practice mode (Doc 8)
  dc_under13_practice: 1.25,

  // Badge XP
  badge_common: 10,
  badge_uncommon: 25,
  badge_rare: 50,
  badge_epic: 100,
  badge_legendary: 250,

  // Streak multipliers (Doc 8)
  streak_4_week_multiplier: 1.1,
  streak_12_week_multiplier: 1.2,
}

/**
 * Anti-gaming rate limits to prevent XP farming (per Doc 8: Gamification System).
 */
export const RATE_LIMITS = {
  max_verifications_per_day: 10,
  max_dc_rounds_per_week: 5,
  max_streak_multiplier: 1.2,
  /** Maximum XP that can be earned in a single calendar day */
  daily_xp_cap: 500,
  /** Minimum seconds between identical challenge_completed events */
  duplicate_completion_window_hours: 24,
}

/**
 * Streak configuration (per Doc 8: Gamification System)
 */
export const STREAK_CONFIG = {
  /** Grace period in days after streak window expires */
  gracePeriodDays: 3,
  /** Streak milestone thresholds and XP bonuses */
  milestones: [
    { weeks: 2, xpBonus: 25 },
    { weeks: 4, xpBonus: 50 },
    { weeks: 8, xpBonus: 75 },
    { weeks: 12, xpBonus: 100 },
    { weeks: 26, xpBonus: 150 },
    { weeks: 52, xpBonus: 500 },
  ],
}

/**
 * Determines the current {@link LevelDefinition} for a given cumulative XP total.
 *
 * @param xp - The user's total cumulative XP
 * @returns The matching level definition (falls back to Level 1 for XP below 0)
 */
export function calculateLevel(xp: number): LevelDefinition {
  for (let i = LEVEL_DEFINITIONS.length - 1; i >= 0; i--) {
    if (xp >= LEVEL_DEFINITIONS[i].minXp) {
      return LEVEL_DEFINITIONS[i]
    }
  }
  return LEVEL_DEFINITIONS[0]
}

/**
 * Calculates the percentage progress within the current level and XP remaining to the next level.
 *
 * @param xp - The user's total cumulative XP
 * @returns `progress` (0-100 percentage) and `toNext` (XP remaining; 0 at max level)
 *
 * @example
 * ```ts
 * const { progress, toNext } = calculateXpProgress(500)
 * // progress: 30 (30% through Level 3: Explorer)
 * // toNext: 350 (need 350 more XP for Level 4)
 * ```
 */
export function calculateXpProgress(xp: number): { progress: number; toNext: number } {
  const currentLevel = calculateLevel(xp)
  const nextLevel = LEVEL_DEFINITIONS.find((l) => l.level === currentLevel.level + 1)

  if (!nextLevel) {
    return { progress: 100, toNext: 0 }
  }

  const levelXp = xp - currentLevel.minXp
  const levelRange = nextLevel.minXp - currentLevel.minXp
  const progress = Math.min(100, Math.round((levelXp / levelRange) * 100))
  const toNext = nextLevel.minXp - xp

  return { progress, toNext }
}
